import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import useSyncSetting from "../Orders/useSyncSetting";
import "./MenuUpdater.scss";
import {
  DURATIONS,
  untilFor,
  readStock,
  collectOutOfStock,
  markOutOfStock,
  markInStock,
  formatRemaining,
  formatAge,
  displayName,
} from "../utils/outOfStock";

const CAT_ALL = "__ALL__";
const CAT_EDITED = "__EDITED__";

export default function MenuUpdater(props) {
  const r_id = props.match.params.r_id;
  const [menu, setMenu] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [menuRef, menuValue] = useSyncSetting(r_id, "menu", false, {});
  const [search, setSearch] = useState("");

  const menuLink = `https://afoodapart.com/api/menu/${r_id}`;

  // fetch menu
  useEffect(() => {
    fetch(menuLink)
      .then((response) => response.json())
      .then((data) => {
        setMenu(data?.menu?.subMenus || []);
      })
      .catch((e) => console.error("Menu fetch failed", e));
  }, [menuLink]);

  const isLoading = menu.length === 0;

  // Helpers
  const isItemEdited = useCallback(
    (itemId) => {
      const overrides = menuValue?.[itemId];
      if (!overrides) return false;
      if (overrides?.name?.en) return true;
      if (overrides?.description?.en !== undefined) return true; // description.en override
      if (overrides?.price !== undefined && overrides?.price !== null) return true;
      return false;
    },
    [menuValue]
  );

  const flattenAllProducts = useCallback(() => {
    const list = [];
    menu.forEach((cat) => {
      (cat.products || []).forEach((item) => list.push(item));
    });
    return list;
  }, [menu]);

  // Search uses effective values; description falls back to item.description.en
  const searchResult = useMemo(() => {
    if (!search || search.length < 3 || menu.length === 0) return [];
    const term = search.toLowerCase();
    const results = [];
    menu.forEach((category) => {
      (category.products || []).forEach((item) => {
        const effectiveName =
          menuValue?.[item.id]?.name?.en ?? item?.name?.en ?? "";
        const effectiveDesc =
          menuValue?.[item.id]?.description?.en ?? item?.description?.en ?? "";
        if (
          effectiveName.toLowerCase().includes(term) ||
          effectiveDesc.toLowerCase().includes(term)
        ) {
          results.push(item);
        }
      });
    });
    return results;
  }, [search, menu, menuValue]);

  const selectedCategoryTitle = useMemo(() => {
    if (!selectedCategory) return "";
    if (selectedCategory === CAT_ALL) return "All Products";
    if (selectedCategory === CAT_EDITED) return "Edited Items";
    const cat = menu.find((c) => c.id === selectedCategory);
    return cat?.name?.en || "";
  }, [selectedCategory, menu]);

  const selectedCategoryItems = useMemo(() => {
    if (!selectedCategory || isLoading) return [];
    if (selectedCategory === CAT_ALL) {
      return flattenAllProducts();
    }
    if (selectedCategory === CAT_EDITED) {
      return flattenAllProducts().filter((item) => isItemEdited(item.id));
    }
    const cat = menu.find((c) => c.id === selectedCategory);
    return cat ? (cat.products || []) : [];
  }, [selectedCategory, isLoading, flattenAllProducts, isItemEdited, menu]);

  return (
    <div className="menu-updater">
      <div className="navigation">
        <Link to={`/r/${r_id}`}>Orders</Link>
        <div className="path-separator">/</div>
        <h1>Menu Updater for {r_id}</h1>
      </div>

      <div className="search">
        <input
          type="text"
          placeholder="Search for items (min 3 chars)"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      {/* Search results */}
      {search.length >= 3 && (
        <div className="search-result">
          <h2 className="section-heading">Search Result</h2>
          <ul>
            {searchResult.map((item) => (
              <MenuItemQuickEditRow
                key={item.id}
                item={item}
                menuValue={menuValue}
                menuRef={menuRef}
              />
            ))}
          </ul>
        </div>
      )}

      {/* Out of stock section */}
      <OutOfStockSection menu={menu} menuValue={menuValue} menuRef={menuRef} />

      {/* Categories */}
      {!selectedCategory ? (
        <div className="categories">
          <h2>Categories</h2>
          {isLoading ? (
            <div>Loading Menu...</div>
          ) : (
            <ul className="category-button-list">
              {/* Synthetic: All Products */}
              <li className="category-button" key={CAT_ALL}>
                <a
                  href="#"
                  className="category-button-link"
                  onClick={(e) => {
                    e.preventDefault();
                    setSelectedCategory(CAT_ALL);
                  }}
                >
                  <div className="category-button-text">All Products</div>
                </a>
              </li>
              {/* Synthetic: Edited Items */}
              <li className="category-button" key={CAT_EDITED}>
                <a
                  href="#"
                  className="category-button-link"
                  onClick={(e) => {
                    e.preventDefault();
                    setSelectedCategory(CAT_EDITED);
                  }}
                >
                  <div className="category-button-text">Edited Items</div>
                </a>
              </li>
              {/* Real categories */}
              {menu.map((category) => (
                <li className="category-button" key={category.id}>
                  <a
                    href="#"
                    className="category-button-link"
                    onClick={(e) => {
                      e.preventDefault();
                      setSelectedCategory(category.id);
                    }}
                  >
                    <div className="category-button-text">{category.name.en}</div>
                  </a>
                </li>
              ))}
            </ul>
          )}
        </div>
      ) : null}

      {/* Selected category view */}
      <div>
        {selectedCategory ? (
          <div>
            <div className="navigation">
              <a
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  setSelectedCategory(null);
                }}
              >
                ALL Categories
              </a>
              <div className="path-separator">/</div>
              <h1>{selectedCategoryTitle || (isLoading ? "Loading…" : "")}</h1>
            </div>
            <ul>
              {selectedCategoryItems.map((item) => (
                <MenuItemQuickEditRow
                  key={`${item.id}`}
                  item={item}
                  menuValue={menuValue}
                  menuRef={menuRef}
                />
              ))}
              {!isLoading && selectedCategoryItems.length === 0 && (
                <li style={{ padding: 8, color: "#6b7280" }}>No items.</li>
              )}
            </ul>
          </div>
        ) : null}
      </div>
    </div>
  );
}

/*
  Reads straight from the overrides rather than walking the fetched menu, so items
  whose id has since disappeared from the menu API still show up here. Those were
  invisible before, which meant nobody could put them back.
*/
function OutOfStockSection({ menu, menuValue, menuRef }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 30 * 1000);
    return () => clearInterval(t);
  }, []);

  const itemsById = useMemo(() => {
    const map = {};
    menu.forEach((cat) => {
      (cat.products || []).forEach((p) => {
        map[p.id] = { name: p?.name?.en || null, category: cat?.name?.en || null };
      });
    });
    return map;
  }, [menu]);

  const outOfStock = useMemo(
    () => collectOutOfStock(menuValue, now),
    [menuValue, now]
  );

  // Items marked before names were stored: fill the name in from the menu we
  // already fetched so the orders screen has something to show. No timestamp is
  // invented for them, since we genuinely don't know when they were marked.
  const latestMenuRef = useRef(menuRef);
  latestMenuRef.current = menuRef;

  const missingNames = useMemo(
    () =>
      outOfStock
        .filter((entry) => !entry.name && itemsById[entry.itemId]?.name)
        .map((entry) => entry.itemId),
    [outOfStock, itemsById]
  );
  const missingNamesKey = missingNames.join(",");

  // Same sweep as the orders screen, so timers still expire on shops whose
  // tablet isn't sitting on the orders page.
  const expiredKey = outOfStock
    .filter((entry) => entry.expired)
    .map((entry) => entry.itemId)
    .join(",");

  useEffect(() => {
    if (!expiredKey) return;
    console.log("out of stock timers expired", expiredKey);
    expiredKey.split(",").forEach((itemId) => {
      markInStock(latestMenuRef.current, itemId);
    });
  }, [expiredKey]);

  useEffect(() => {
    if (!missingNamesKey) return;
    missingNamesKey.split(",").forEach((itemId) => {
      const name = itemsById[itemId]?.name;
      if (!name) return;
      latestMenuRef.current.child(itemId).child("outOfStockName").set(name);
    });
    // itemsById is read through the key above, which only changes when the set does
  }, [missingNamesKey]);

  if (outOfStock.length === 0) return null;

  return (
    <div className="out-of-stock-items">
      <h2 className="section-heading">
        Currently Out of Stock ({outOfStock.length})
      </h2>
      <ul className="oos-list">
        {outOfStock.map((entry) => {
          const known = itemsById[entry.itemId];
          const name = entry.name || known?.name || entry.itemId;
          return (
            <li className="oos-row" key={entry.itemId} data-state={entry.forever ? "forever" : entry.legacy ? "legacy" : "timed"}>
              <div className="oos-row-main">
                <span className="oos-row-name">{name}</span>
                {known?.category ? (
                  <span className="oos-row-category">{known.category}</span>
                ) : (
                  <span className="oos-row-category oos-row-gone">
                    no longer on menu
                  </span>
                )}
              </div>
              <div className="oos-row-timing">
                {entry.forever ? (
                  <span className="oos-tag forever">forever</span>
                ) : entry.until ? (
                  <span className="oos-tag timed">
                    back in {formatRemaining(entry.until, now)}
                  </span>
                ) : (
                  <span className="oos-tag legacy">
                    no timer · out {formatAge(entry.since, now)}
                  </span>
                )}
              </div>
              <button
                className="oos-restore"
                onClick={() => markInStock(menuRef, entry.itemId)}
              >
                Back in stock
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

/*
  Marking something out always carries an expiry, so a forgotten item puts itself
  back. Items already marked with no end date still show here, but staff can't
  create new ones.
*/
function StockControl({ item, overrides, menuRef, name }) {
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 30 * 1000);
    return () => clearInterval(t);
  }, []);

  const stock = readStock(overrides, now);

  if (stock) {
    return (
      <div className="stock-control is-out">
        <div className="stock-status">
          <span className="stock-status-label">OUT</span>
          {stock.forever ? (
            <span className="stock-status-detail">no end date</span>
          ) : stock.until ? (
            <span className="stock-status-detail">
              back in {formatRemaining(stock.until, now)}
            </span>
          ) : (
            <span className="stock-status-detail is-warning">
              no timer · out {formatAge(stock.since, now)}
            </span>
          )}
        </div>
        <button
          className="stock-button restore"
          onClick={() => markInStock(menuRef, item.id)}
        >
          Back in stock
        </button>
      </div>
    );
  }

  return (
    <div className="stock-control">
      <span className="stock-control-label">Mark out for:</span>
      {DURATIONS.map((duration) => (
        <button
          key={duration.key}
          className="stock-button duration"
          onClick={() =>
            markOutOfStock(menuRef, item.id, {
              until: untilFor(duration, Date.now()),
              name,
            })
          }
        >
          {duration.label}
        </button>
      ))}
    </div>
  );
}

function MenuItemQuickEditRow({ item, menuValue, menuRef }) {
  const overrides = menuValue?.[item.id] || {};

  const effectiveName = overrides?.name?.en ?? item?.name?.en ?? "";
  const effectivePrice = overrides?.price ?? item?.price ?? "";
  const effectiveDesc = overrides?.description?.en ?? item?.description?.en ?? "";

  const [nameEn, setNameEn] = useState(effectiveName);
  const [price, setPrice] = useState(
    effectivePrice === null || typeof effectivePrice === "undefined"
      ? ""
      : effectivePrice
  );
  const [description, setDescription] = useState(effectiveDesc);
  const [isEditing, setIsEditing] = useState(false);

  // Keep local state in sync if overrides change elsewhere
  useEffect(() => {
    setNameEn(overrides?.name?.en ?? item?.name?.en ?? "");
  }, [overrides?.name?.en, item?.name?.en]);

  useEffect(() => {
    setPrice(overrides?.price ?? item?.price ?? "");
  }, [overrides?.price, item?.price]);

  useEffect(() => {
    setDescription(overrides?.description?.en ?? item?.description?.en ?? "");
  }, [overrides?.description?.en, item?.description?.en]);

  // Debounce helpers
  const debounceTimers = useRef({});
  const saveField = useCallback(
    (segments, value) => {
      try {
        let ref = menuRef.child(item.id);
        segments.forEach((seg) => {
          ref = ref.child(seg);
        });
        if (value === "" || value === null || typeof value === "undefined") {
          ref.remove();
        } else {
          ref.set(value);
        }
      } catch (e) {
        console.error("Save failed", segments, e);
      }
    },
    [item.id, menuRef]
  );

  const debouncedSave = useCallback(
    (key, segments, value, delay = 400) => {
      if (debounceTimers.current[key]) {
        clearTimeout(debounceTimers.current[key]);
      }
      debounceTimers.current[key] = setTimeout(
        () => saveField(segments, value),
        delay
      );
    },
    [saveField]
  );

  // Edit handlers (only active in edit mode)
  const onNameChange = (e) => {
    const v = e.target.value;
    setNameEn(v);
    debouncedSave("name.en", ["name", "en"], v);
  };
  const onPriceChange = (e) => {
    const raw = e.target.value;
    let toSave = raw;
    if (raw !== "") {
      const n = Number(raw);
      toSave = Number.isFinite(n) ? n : raw;
    }
    setPrice(raw);
    debouncedSave("price", ["price"], toSave);
  };
  const onDescriptionChange = (e) => {
    const v = e.target.value;
    setDescription(v);
    // SAVE DESCRIPTION AT description/en
    debouncedSave("description.en", ["description", "en"], v);
  };

  const resetName = (e) => {
    e.preventDefault();
    menuRef.child(item.id).child("name").child("en").remove();
  };
  const resetPrice = (e) => {
    e.preventDefault();
    menuRef.child(item.id).child("price").remove();
  };
  const resetDescription = (e) => {
    e.preventDefault();
    // REMOVE ONLY description/en override
    menuRef.child(item.id).child("description").child("en").remove();
  };

  // Display helpers
  const displayPrice = (p) => {
    if (p === "" || p === null || typeof p === "undefined") return "";
    const n = Number(p);
    return Number.isFinite(n) ? `$${n.toFixed(2)}` : String(p);
  };

  // Detect modifier overrides (for red highlight)
  const hasModifierOverrides =
    !!overrides?.modgroups &&
    Object.values(overrides.modgroups).some((mg) => {
      if (!mg?.modifiers) return false;
      return Object.values(mg.modifiers).some((m) => {
        if (!m) return false;
        return (
          m.name !== undefined ||
          (m.price !== undefined && m.price !== null) ||
          m.description !== undefined
        );
      });
    });

  // Edited flag for styling (turns red when any override exists)
  const isEdited =
    !!overrides?.name?.en ||
    (overrides?.price !== undefined && overrides?.price !== null) ||
    overrides?.description?.en !== undefined ||
    hasModifierOverrides;

  return (
    <li className="menu-item">
      <div className="menu-item-row" data-edited={isEdited ? "true" : "false"}>
        {/* Stock controls are always visible */}
        <StockControl
          item={item}
          overrides={overrides}
          menuRef={menuRef}
          name={effectiveName}
        />

        {/* Collapsed view with inline Edit + smaller price */}
        {!isEditing && (
          <div className="menu-item-display">
            <div className="menu-item-headline">
              <h3 className="menu-item-name">{effectiveName}</h3>
              <div className="menu-item-price">
                {displayPrice(effectivePrice)}
              </div>
              <div className="menu-item-actions">
                <button className="btn-edit" onClick={() => setIsEditing(true)}>
                  Edit
                </button>
              </div>
            </div>
            {effectiveDesc ? (
              <p className="menu-item-desc">
                {String(effectiveDesc).length > 140
                  ? String(effectiveDesc).slice(0, 140) + "…"
                  : String(effectiveDesc)}
              </p>
            ) : null}
          </div>
        )}

        {/* Editor view (compact) */}
        {isEditing && (
          <div className="menu-item-fields">
            <div className="field-group">
              <label className="field-label">Name</label>
              <div className="field-inline">
                <input
                  className="field-input"
                  type="text"
                  value={nameEn}
                  onChange={onNameChange}
                  placeholder="Item name"
                />
                <button
                  className="field-reset"
                  onClick={resetName}
                  title="Revert to API value"
                >
                  Reset
                </button>
              </div>
            </div>

            <div className="field-group">
              <label className="field-label">Price</label>
              <div className="field-inline">
                <input
                  className="field-input"
                  type="number"
                  step="0.01"
                  inputMode="decimal"
                  value={
                    price === null || typeof price === "undefined"
                      ? ""
                      : String(price)
                  }
                  onChange={onPriceChange}
                  placeholder="0.00"
                />
                <button
                  className="field-reset"
                  onClick={resetPrice}
                  title="Revert to API value"
                >
                  Reset
                </button>
              </div>
            </div>

            <div className="field-group">
              <label className="field-label">Description</label>
              <div className="field-inline">
                <textarea
                  className="field-textarea"
                  value={description}
                  onChange={onDescriptionChange}
                  placeholder="Description (saved to description.en)"
                  rows={2}
                />
                <button
                  className="field-reset"
                  onClick={resetDescription}
                  title="Revert to API value"
                >
                  Reset
                </button>
              </div>
            </div>

            {/* NEW: buffet modifiers editor (only type="buffet") */}
            <BuffetModifiersEditor
              item={item}
              overrides={overrides}
              debouncedSave={debouncedSave}
              menuRef={menuRef}
            />

            <div className="menu-item-actions">
              <button className="btn-done" onClick={() => setIsEditing(false)}>
                Done
              </button>
            </div>
          </div>
        )}
      </div>
    </li>
  );
}

/**
 * Edits buffet-type modgroups under:
 * menu/{r_id}/{itemId}/modgroups/{modgroupId}/modifiers/{modifierId}/{field}
 *
 * Only cares about "buffet" modgroups. Others are read-only and ignored here.
 */
function BuffetModifiersEditor({ item, overrides, debouncedSave, menuRef }) {
  const modgroups = Array.isArray(item.modgroups) ? item.modgroups : [];
  const buffetGroups = modgroups.filter(
    (mg) =>
      mg &&
      mg.type === "buffet" &&
      Array.isArray(mg.modifiers) &&
      mg.modifiers.length > 0
  );

  if (buffetGroups.length === 0) return null;

  return (
    <div className="field-group buffet-modifiers-editor">
      <label className="field-label">Buffet Modifiers</label>
      {buffetGroups.map((mg) => (
        <div className="buffet-modgroup-editor" key={mg.id}>
          <div className="buffet-modgroup-title">
            {mg.name || mg.id || "Buffet Group"}
          </div>
          <ul className="buffet-modifier-list">
            {mg.modifiers.map((mod) => (
              <BuffetModifierRow
                key={mod.id}
                itemId={item.id}
                modgroup={mg}
                modifier={mod}
                override={
                  overrides?.modgroups?.[mg.id]?.modifiers?.[mod.id] || {}
                }
                debouncedSave={debouncedSave}
                menuRef={menuRef}
              />
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
}

function BuffetModifierRow({
  itemId,
  modgroup,
  modifier,
  override,
  debouncedSave,
  menuRef,
}) {
  const effectiveName = override?.name ?? modifier.name ?? modifier.id ?? "";
  const effectivePrice =
    override?.price ??
    (typeof modifier.price !== "undefined" ? modifier.price : "");
  const effectiveDesc =
    override?.description ??
    (typeof modifier.description !== "undefined"
      ? modifier.description
      : "");

  const [name, setName] = useState(effectiveName);
  const [price, setPrice] = useState(
    effectivePrice === null || typeof effectivePrice === "undefined"
      ? ""
      : effectivePrice
  );
  const [description, setDescription] = useState(effectiveDesc);

  useEffect(() => {
    setName(override?.name ?? modifier.name ?? modifier.id ?? "");
  }, [override?.name, modifier.name, modifier.id]);

  useEffect(() => {
    setPrice(
      override?.price ??
        (typeof modifier.price !== "undefined" ? modifier.price : "")
    );
  }, [override?.price, modifier.price]);

  useEffect(() => {
    setDescription(
      override?.description ??
        (typeof modifier.description !== "undefined"
          ? modifier.description
          : "")
    );
  }, [override?.description, modifier.description]);

  const pathBase = ["modgroups", modgroup.id, "modifiers", modifier.id];

  const onNameChange = (e) => {
    const v = e.target.value;
    setName(v);
    debouncedSave(
      `mod-${modgroup.id}-${modifier.id}-name`,
      [...pathBase, "name"],
      v
    );
  };

  const onPriceChange = (e) => {
    const raw = e.target.value;
    let toSave = raw;
    if (raw !== "") {
      const n = Number(raw);
      toSave = Number.isFinite(n) ? n : raw;
    }
    setPrice(raw);
    debouncedSave(
      `mod-${modgroup.id}-${modifier.id}-price`,
      [...pathBase, "price"],
      toSave
    );
  };

  const onDescriptionChange = (e) => {
    const v = e.target.value;
    setDescription(v);
    debouncedSave(
      `mod-${modgroup.id}-${modifier.id}-description`,
      [...pathBase, "description"],
      v
    );
  };

  const resetName = (e) => {
    e.preventDefault();
    menuRef
      .child(itemId)
      .child("modgroups")
      .child(modgroup.id)
      .child("modifiers")
      .child(modifier.id)
      .child("name")
      .remove();
  };

  const resetPrice = (e) => {
    e.preventDefault();
    menuRef
      .child(itemId)
      .child("modgroups")
      .child(modgroup.id)
      .child("modifiers")
      .child(modifier.id)
      .child("price")
      .remove();
  };

  const resetDescription = (e) => {
    e.preventDefault();
    menuRef
      .child(itemId)
      .child("modgroups")
      .child(modgroup.id)
      .child("modifiers")
      .child(modifier.id)
      .child("description")
      .remove();
  };

  return (
    <li className="buffet-modifier-row">
      <div className="buffet-modifier-header">
        <span className="buffet-modifier-id">{modifier.id}</span>
      </div>

      <div className="field-inline small">
        <input
          className="field-input"
          type="text"
          value={name}
          onChange={onNameChange}
          placeholder="Modifier name"
        />
        <button
          className="field-reset"
          onClick={resetName}
          title="Revert to API value"
        >
          Reset
        </button>
      </div>

      <div className="field-inline small">
        <input
          className="field-input"
          type="number"
          step="0.01"
          inputMode="decimal"
          value={
            price === null || typeof price === "undefined" ? "" : String(price)
          }
          onChange={onPriceChange}
          placeholder="Price"
        />
        <button
          className="field-reset"
          onClick={resetPrice}
          title="Revert to API value"
        >
          Reset
        </button>
      </div>

      <div className="field-inline small">
        <textarea
          className="field-textarea"
          rows={2}
          value={description || ""}
          onChange={onDescriptionChange}
          placeholder="Modifier description"
        />
        <button
          className="field-reset"
          onClick={resetDescription}
          title="Revert to API value"
        >
          Reset
        </button>
      </div>
    </li>
  );
}